"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";

import { clienteArquivado } from "@/lib/clientes/arquivo";
import { ErroConfiguracao } from "@/lib/config/erro";
import { prisma } from "@/lib/db";
import { emailDoApp } from "@/lib/email/config";
import { resumirErro } from "@/lib/fila";
import { consumirLink, destinoSeguro, solicitarLink } from "./login";
import { entrarComSenha } from "./login-senha";
import { lerLimites, mensagemDeLimite, minutosAte } from "./rate-limit";
import { COOKIE_CLIENTE, COOKIE_SESSAO, encerrarSessao, opcoesCookieSessao } from "./sessao";
import { exigirSessao, ipDaRequisicao, urlBase } from "./servidor";

export interface EstadoPedido {
  mensagem: string;
  /** So no Dev com EMAIL_MODO simulado: o link que "seria enviado" por e-mail. */
  linkDev?: string;
}

const MENSAGEM_GENERICA = "Se o e-mail estiver cadastrado, enviamos um link de acesso. Ele vale por 30 minutos.";

export async function pedirLink(_anterior: EstadoPedido | null, dados: FormData): Promise<EstadoPedido> {
  const envio = await emailDoApp(prisma);
  let base: string;
  try {
    base = await urlBase();
  } catch (erro) {
    if (!(erro instanceof ErroConfiguracao)) throw erro;
    console.error("[login] configuracao:", resumirErro(erro));
    return { mensagem: "O acesso por link ainda nao esta configurado neste servidor. Avise a agencia DKP." };
  }
  const agora = new Date();
  const r = await solicitarLink(prisma, {
    email: String(dados.get("email") ?? ""),
    ip: await ipDaRequisicao(),
    urlBase: base,
    envio,
    limites: lerLimites(),
    agora,
  });
  if (r.resultado === "invalido") return { mensagem: "Informe um e-mail valido." };
  if (r.resultado === "limite") return { mensagem: mensagemDeLimite(minutosAte(r.liberaEm, agora)) };
  const dev = r.resultado === "enviado" && envio.modo === "simulado" && process.env.NODE_ENV !== "production";
  return dev ? { mensagem: MENSAGEM_GENERICA, linkDev: r.link } : { mensagem: MENSAGEM_GENERICA };
}

const ERRO_SENHA = "E-mail ou senha invalidos.";

/** Aba "Senha" de /login (EST-29): so agencia ativa com senha; toda falha tem a mesma mensagem. Mesma sessao e cookie do link. */
export async function pedirEntradaComSenha(_anterior: EstadoPedido | null, dados: FormData): Promise<EstadoPedido> {
  const agora = new Date();
  let r;
  try {
    r = await entrarComSenha(prisma, {
      email: String(dados.get("email") ?? ""),
      senha: String(dados.get("senha") ?? ""),
      ip: await ipDaRequisicao(),
      limites: lerLimites(),
      agora,
    });
  } catch (erro) {
    console.error("[login] senha:", resumirErro(erro));
    return { mensagem: ERRO_SENHA };
  }
  if (!r.ok) return { mensagem: r.motivo === "limite" ? mensagemDeLimite(minutosAte(r.liberaEm, agora)) : ERRO_SENHA };
  const jar = await cookies();
  jar.set(COOKIE_SESSAO, r.segredoSessao, opcoesCookieSessao());
  redirect("/calendario");
}

/** Botao "Entrar" de /auth/verificar (POST: previews de link no WhatsApp/e-mail nao gastam o token). */
export async function entrar(dados: FormData): Promise<void> {
  const r = await consumirLink(prisma, { token: dados.get("token"), ip: await ipDaRequisicao() });
  if (!r.ok) redirect("/login?erro=link");
  const jar = await cookies();
  jar.set(COOKIE_SESSAO, r.segredoSessao, opcoesCookieSessao());
  const destino = dados.get("destino");
  redirect(destinoSeguro(destino) ? destino : "/calendario");
}

// O cookie `estudio_cliente` (so um id, validado no servidor a cada uso) sobrevive ao login e ao sair: a agencia
// volta ao ultimo cliente escolhido (EST-36). Para o papel cliente ele e ignorado.
export async function sair(): Promise<void> {
  const jar = await cookies();
  await encerrarSessao(prisma, jar.get(COOKIE_SESSAO)?.value, await ipDaRequisicao());
  jar.delete(COOKIE_SESSAO);
  redirect("/login?saiu=1");
}

/** Seletor do topo: so a agencia troca; o id e validado contra o banco. */
export async function trocarCliente(dados: FormData): Promise<void> {
  const sessao = await exigirSessao();
  const clienteId = String(dados.get("clienteId") ?? "");
  if (sessao.usuario.papel === "agencia" && (await prisma.cliente.findUnique({ where: { id: clienteId } })) && !(await clienteArquivado(prisma, clienteId))) {
    (await cookies()).set(COOKIE_CLIENTE, clienteId, opcoesCookieSessao());
  }
  redirect("/calendario");
}
